import { useEffect, useRef } from "react";
import "./Process.css";

export default function Process() {
  const sectionRef = useRef(null);

  const workSteps = [
    { t: "Profile Setup", d: "Optimize bios, images, and platform branding effectively." },
    { t: "Content Planning", d: "Build calendars around trends, goals, and engagement intent." },
    { t: "Design & Writing", d: "Create engaging visuals and captions for posting." },
    { t: "Publishing", d: "Schedule content consistently using trusted automation tools." },
    { t: "Community Management", d: "Engage with followers and manage direct messages daily." },
    { t: "Analytics & Reporting", d: "Track growth and improve with performance-based insights." },
  ];

  const industries = [
    "Automotive",
    "Retail",
    "Healthcare",
    "B2B",
    "Education",
    "Construction",
    "Energy",
    "Import & Export",
    "Science",
    "Travel & Tourism",
  ];

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const raf = requestAnimationFrame(() => el.classList.add("is-mounted"));

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("is-visible");
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);

    // reduced motion check
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mql.matches) {
      return () => {
        cancelAnimationFrame(raf);
        io.disconnect();
      };
    }

    // subtle parallax for background
    const setParallax = (x, y) => {
      el.style.setProperty("--mx", String(x));
      el.style.setProperty("--my", String(y));
    };
    setParallax(0.5, 0.5);

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width;
      const y = (e.clientY - r.top) / r.height;
      const cx = Math.max(0, Math.min(1, x));
      const cy = Math.max(0, Math.min(1, y));
      setParallax(cx.toFixed(4), cy.toFixed(4));
    };

    const onLeave = () => setParallax(0.5, 0.5);

    el.addEventListener("pointermove", onMove, { passive: true });
    el.addEventListener("pointerleave", onLeave);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <section id="process" className="process" ref={sectionRef}>
      {/* decorative layer */}
      <div className="process__bg" aria-hidden="true">
        <span className="process__orb process__orb--1" />
        <span className="process__orb process__orb--2" />
        <span className="process__grid" />
      </div>

      <div className="container">
        {/* HOW WE WORK */}
        <header className="process__head">
          <h2 className="process__title reveal" style={{ "--d": "0ms" }}>
            How We Work
          </h2>
          <p className="process__subtitle reveal" style={{ "--d": "90ms" }}>
            Our streamlined process ensures your marketing is planned, created, and optimized for success.
          </p>
        </header>

        <div className="work__grid">
          {workSteps.map((x, i) => (
            <div className="workCard reveal" style={{ "--d": `${180 + i * 90}ms` }} key={x.t}>
              <div className="workCard__top">
                <h3 className="workCard__title">{x.t}</h3>
              </div>
              <div className="workCard__bottom">
                <p className="workCard__desc">{x.d}</p>
              </div>
            </div>
          ))}
        </div>

        {/* INDUSTRIES */}
        <div className="industries">
          <header className="industries__head">
            <h2 className="industries__title reveal" style={{ "--d": "0ms" }}>
              Customized Solutions for Every Industry
            </h2>
            <p className="industries__subtitle reveal" style={{ "--d": "90ms" }}>
              We create tailored strategies to help businesses thrive, no matter their industry or challenges.
            </p>
          </header>

          <div className="industries__grid" role="list">
            {industries.map((name, i) => (
              <div
                className="industryTile reveal"
                style={{ "--d": `${140 + i * 55}ms` }}
                key={name}
                role="listitem"
              >
                <span className="industryTile__text">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
